import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { Activity, CheckCircle2, ExternalLink, FileArchive, GitCompareArrows, Link2, Loader, Package, Rocket, Sparkles } from 'lucide-react';
import { partnersApi } from '../../services/partnersApi';
import { PartnerCurseForgeLinkPicker } from './PartnerCurseForgeLinkPicker';
import { PartnerUpdateStatusControl } from './PartnerUpdateStatusControl';
import { VersionTriad } from './VersionTriad';
import { ReportSourceBadge, ReportStatusBadge } from './PartnerPhaseBadge';
import type { PartnerActivity, PartnerPack, ReportView } from '../../types/partners';
import { formatBytes } from '../../utils/dataControllerFormat';
import { partnerPagePath } from '../../utils/partnerLinks';
import { releaseStateLabel, worldCompatLabel } from '../../utils/partnerStatus';
import { describeReportOutcome, type PartnerNotice } from '../../utils/partnerSync';

interface PartnerReportDetailProps {
  report: ReportView;
  /**
   * The partner's packs, when the page already has them. Without them (the
   * inbox), they are fetched once per partner, for the version comparison.
   */
  packs?: PartnerPack[];
  canEdit: boolean;
  /** The viewer can open Modpack Configs, for "Update the cluster". */
  canOpenConfigs?: boolean;
  onSaved: (report: ReportView) => void;
  onNotice: (notice: PartnerNotice) => void;
}

// Opening several reports from one partner in the inbox fetches their packs once.
const PACKS_TTL_MS = 60_000;
const packsCache = new Map<string, { at: number; packs: Promise<PartnerPack[]> }>();
function packsFor(creatorId: string): Promise<PartnerPack[]> {
  const hit = packsCache.get(creatorId);
  if (hit && Date.now() - hit.at < PACKS_TTL_MS) return hit.packs;
  const packs = partnersApi.get(creatorId).then((d) => d.packs);
  packsCache.set(creatorId, { at: Date.now(), packs });
  packs.catch(() => packsCache.delete(creatorId));
  return packs;
}

const ago = (ms: number | null | undefined) => (ms ? formatDistanceToNow(new Date(ms), { addSuffix: true }) : null);
const safeLink = (url: string | null) => (url && /^https:\/\/[^\s]+$/i.test(url) ? url : null);

/**
 * One update report in full: what the partner said (as plain text, since it
 * is theirs), where it stands, CurseForge and live detection, any server
 * pack that came with it, and its timeline.
 */
export const PartnerReportDetail = ({ report, packs: givenPacks, canEdit, canOpenConfigs = false, onSaved, onNotice }: PartnerReportDetailProps) => {
  const [fetchedPacks, setFetchedPacks] = useState<PartnerPack[] | null>(null);
  const [pickingFile, setPickingFile] = useState(false);
  const packs = givenPacks ?? fetchedPacks ?? undefined;
  const pack = packs?.find((p) => p.modpack_id === report.modpack_id) ?? null;

  useEffect(() => {
    if (givenPacks) return;
    let cancelled = false;
    packsFor(report.creator_id)
      .then((list) => {
        if (!cancelled) setFetchedPacks(list);
      })
      .catch(() => {
        // Only the version comparison and pack filing need these; the report still shows.
      });
    return () => {
      cancelled = true;
    };
  }, [givenPacks, report.creator_id]);

  const [events, setEvents] = useState<PartnerActivity[] | null>(null);
  const [eventsError, setEventsError] = useState<string | null>(null);
  const [filing, setFiling] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setEventsError(null);
    partnersApi
      .report(report.id)
      .then((res) => {
        if (!cancelled) setEvents(res.events);
      })
      .catch((e) => {
        if (!cancelled) setEventsError(e instanceof Error ? e.message : 'The report’s timeline could not be loaded.');
      });
    return () => {
      cancelled = true;
    };
  }, [report.id, report.revision]);

  const fileUnder = async (modpackId: string | null) => {
    setFiling(true);
    try {
      const res = await partnersApi.updateReport(report.id, { modpack_id: modpackId });
      onSaved(res.report);
      onNotice(describeReportOutcome(res.discord, modpackId ? `Filed ${report.version} under ${res.report.pack_name}.` : `Took ${report.version} off its pack.`));
    } catch (e) {
      onNotice({ tone: 'danger', text: e instanceof Error ? e.message : 'The pack could not be changed.' });
    } finally {
      setFiling(false);
    }
  };

  const changelog = safeLink(report.changelog_url);

  return (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="subcard p-3 md:p-4 space-y-3 min-w-0">
          <p className="eyebrow">What was reported</p>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-white">{report.pack_name}</span>
            <span className="font-mono text-sm text-accent-300">{report.version}</span>
            <ReportSourceBadge source={report.source} />
          </div>
          <dl className="grid grid-cols-[8rem_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-sm">
            <dt className="text-glass-400">Release</dt>
            <dd className="text-glass-200">{releaseStateLabel(report.release_state)}</dd>
            <dt className="text-glass-400">Worlds</dt>
            <dd className="text-glass-200">{worldCompatLabel(report.world_compat)}</dd>
            <dt className="text-glass-400">Reported by</dt>
            <dd className="text-glass-200 min-w-0">
              {report.reporter ? report.reporter.name : report.source === 'curseforge' ? 'Argus, from CurseForge' : 'Unknown'}
              {report.reporter?.discord_id && <span className="text-[11px] font-mono text-glass-500"> · {report.reporter.discord_id}</span>}
              <span className="text-glass-500"> · {ago(report.created_at)}</span>
            </dd>
            <dt className="text-glass-400">Changelog</dt>
            <dd className="min-w-0">
              {changelog ? (
                <a href={changelog} target="_blank" rel="noopener noreferrer nofollow" className="link inline-flex items-center gap-1 break-all">
                  <ExternalLink className="w-3 h-3 shrink-0" />
                  {changelog.replace(/^https:\/\//i, '')}
                </a>
              ) : (
                <span className="text-glass-500">None given</span>
              )}
            </dd>
            <dt className="text-glass-400">Visible to</dt>
            <dd className="text-glass-200">{report.partner_visible ? 'The partner, on its card in their channel, and staff' : 'Staff only'}</dd>
          </dl>
          {report.notes && (
            <div>
              <p className="text-xs font-medium text-glass-400 mb-1">Notes</p>
              {/* Partner text is untrusted: plain text only, never markdown or HTML. */}
              <p className="text-sm text-glass-200 whitespace-pre-wrap break-words">{report.notes}</p>
            </div>
          )}
          {packs && packs.length > 0 && canEdit && (
            <div>
              <label htmlFor={`file-${report.id}`} className="block text-xs font-medium text-glass-400 mb-1.5">
                Filed under
              </label>
              <div className="flex items-center gap-2">
                <select
                  id={`file-${report.id}`}
                  className="glass-select flex-1 min-w-0"
                  value={report.modpack_id ?? ''}
                  disabled={filing}
                  onChange={(e) => void fileUnder(e.target.value || null)}
                >
                  <option value="">Not one of the partner’s packs ({report.pack_name})</option>
                  {packs.map((p) => (
                    <option key={p.modpack_id} value={p.modpack_id}>
                      {p.name}
                    </option>
                  ))}
                </select>
                {filing && <Loader className="w-4 h-4 animate-spin text-glass-400" />}
              </div>
            </div>
          )}
        </div>

        <div className="subcard p-3 md:p-4 space-y-3 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p className="eyebrow">Where it stands</p>
            <ReportStatusBadge status={report.status} />
          </div>
          {report.status_changed_at && (
            <p className="text-xs text-glass-500">
              Last moved {ago(report.status_changed_at)}
              {report.status_changed_by_name ? ` by ${report.status_changed_by_name}` : ''}.
            </p>
          )}
          {report.superseded_by && (
            <p className="text-sm text-glass-300">
              A newer report replaced this one.{' '}
              <Link to={partnerPagePath(report.creator_id, 'updates', report.superseded_by)} className="link">
                Open it
              </Link>
            </p>
          )}
          {canEdit ? (
            <PartnerUpdateStatusControl report={report} onSaved={onSaved} onNotice={onNotice} />
          ) : (
            report.status_note && (
              <p className="text-sm text-glass-300 whitespace-pre-wrap break-words">
                {report.status_note}
                <span className="block text-xs text-glass-500 mt-1">{report.status_note_public ? 'The partner sees this note.' : 'Only staff see this note.'}</span>
              </p>
            )
          )}

          <ul className="space-y-1.5 text-sm border-t border-glass-200/10 pt-3">
            <li className="flex items-start gap-2">
              <Sparkles className="w-4 h-4 mt-0.5 text-glass-400 shrink-0" />
              <span className="min-w-0 flex-1">
                {report.curseforge ? (
                  <span className="text-glass-200">
                    On CurseForge as <span className="font-mono break-all">{report.curseforge.display_name ?? `file ${report.curseforge.file_id}`}</span>
                    {report.curseforge.listed_at ? <span className="text-glass-500"> · seen {ago(report.curseforge.listed_at)}</span> : null}
                  </span>
                ) : (
                  <span className="text-glass-400">Not seen on CurseForge yet.</span>
                )}
                {canEdit && pack?.apogee_id && !pickingFile && (
                  <button type="button" className="btn-ghost btn-xs ml-1" onClick={() => setPickingFile(true)}>
                    <Link2 className="w-3 h-3" />
                    {report.curseforge ? 'Change the file' : 'Link a CurseForge file'}
                  </button>
                )}
              </span>
            </li>
            <li className="flex items-start gap-2">
              <Rocket className="w-4 h-4 mt-0.5 text-glass-400 shrink-0" />
              {report.live_at ? (
                <span className="text-glass-200">
                  Live on our servers {ago(report.live_at)}
                  <span className="text-glass-500">
                    {report.live_source === 'configs' ? ', noticed from the cluster version in Modpack Configs' : report.live_source === 'staff' ? ', marked by staff' : ''}
                  </span>
                </span>
              ) : (
                <span className="text-glass-400">Not live on our servers yet. It goes live by itself when a cluster’s version is set to it in Modpack Configs.</span>
              )}
            </li>
            <li className="flex items-start gap-2">
              <FileArchive className="w-4 h-4 mt-0.5 text-glass-400 shrink-0" />
              {report.server_pack ? (
                <span className="text-glass-200 min-w-0">
                  Server pack <span className="font-mono break-all">{report.server_pack.filename}</span>
                  <span className="text-glass-500">
                    {' '}
                    · {formatBytes(report.server_pack.size)} · received {ago(report.server_pack.received_at)} ·{' '}
                  </span>
                  <Link to={partnerPagePath(report.creator_id, 'server-packs')} className="link">
                    Server packs
                  </Link>
                </span>
              ) : (
                <span className="text-glass-400">No server pack shared with this report.</span>
              )}
            </li>
          </ul>
          {pickingFile && pack?.apogee_id && (
            <PartnerCurseForgeLinkPicker
              report={report}
              apogeeId={pack.apogee_id}
              onLinked={onSaved}
              onNotice={onNotice}
              onClose={() => setPickingFile(false)}
            />
          )}
        </div>
      </div>

      {pack && (
        <div className="subcard p-3 md:p-4 space-y-2">
          <p className="eyebrow flex items-center gap-1.5">
            <GitCompareArrows className="w-3.5 h-3.5" /> Versions of {pack.name}
          </p>
          <VersionTriad pack={pack} report={report} canOpenConfigs={canOpenConfigs} />
        </div>
      )}

      <div className="subcard p-3 md:p-4">
        <p className="eyebrow mb-2 flex items-center gap-1.5">
          <Activity className="w-3.5 h-3.5" /> Timeline
        </p>
        {eventsError ? (
          <p className="text-xs text-red-400">{eventsError}</p>
        ) : !events ? (
          <p className="text-xs text-glass-400 flex items-center gap-2">
            <Loader className="w-3.5 h-3.5 animate-spin" /> Loading…
          </p>
        ) : events.length === 0 ? (
          <p className="text-xs text-glass-500">Nothing yet.</p>
        ) : (
          <ul className="space-y-1.5">
            {events.map((e) => (
              <li key={e.id} className="text-sm text-glass-300 flex flex-wrap items-baseline gap-x-2">
                {e.action.endsWith('live') ? <CheckCircle2 className="w-3.5 h-3.5 text-green-400 self-center" /> : e.action.includes('pack') ? <Package className="w-3.5 h-3.5 text-glass-400 self-center" /> : null}
                <span className="break-words">{e.description}</span>
                <span className="text-xs text-glass-500">
                  {e.actor_label ? `${e.actor_label} · ` : ''}
                  {ago(e.created_at)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
